import { ChangeDetectionStrategy, Component, DestroyRef, HostBinding, Input, OnChanges, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { OfferSource } from '@cds';
import { EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { Sitecore, TopEventsConfig } from '@frontend/sports/common/client-config-data-access';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { ParticipantImageService } from '@frontend/sports/participant-image/feature';
import { DeviceService } from '@frontend/vanilla/core';
import { clone, indexOf, sortBy, toNumber } from 'lodash-es';
import { BehaviorSubject, noop } from 'rxjs';

import { SourceOfPick } from '../../betslip-base/models/pick-models';
import { EachWayMarketHelperService } from '../../option-group/each-way-market-helper.service';
import { TopEventsTrackingService } from '../top-events-tracking.service';
import { PodType, TopEventsOutrightData, TopEventsPodData } from '../top-events.model';

@Component({
    selector: 'ms-top-events-outrights',
    templateUrl: 'top-events-outright.component.html',
    styleUrl: 'top-events-outright.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ParticipantImageService],
    standalone: false,
})
export class TopEventsOutrightComponent implements OnInit, OnChanges, OnDestroy {
    @HostBinding('class') className = 'top-events-pod-outright';
    @HostBinding('class.pod-header-hidden') get podHeaderHidden() {
        return this.pod?.hidePodHeader;
    }

    @Input() pod: TopEventsPodData;
    @Input() index: number;
    trackingModuleType = PodType.Outright;
    sourceOfPick = SourceOfPick.OutrightsGrid;
    // Used to manually change the column size from sitecore on mobile device only.
    responsiveColumns = 0;
    vm$ = new BehaviorSubject<TopEventsOutrightData | undefined>(undefined);

    private optionsExpanded = false;
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        public sitecore: Sitecore,
        private deviceService: DeviceService,
        private urlHelper: UrlHelperService,
        private topEventsConfig: TopEventsConfig,
        private topEventsTrackingService: TopEventsTrackingService,
        private participantImageService: ParticipantImageService,
        private eachWayMarketHelper: EachWayMarketHelperService,
    ) {}

    ngOnInit(): void {
        this.deviceService.orientation.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
            if (this.deviceService.isMobilePhone && data === 'portrait') {
                this.responsiveColumns = this.pod.mobileColumns ?? 1;
            } else {
                this.responsiveColumns = 0;
            }
        });
    }

    ngOnChanges(): void {
        this.pod.updateHandler = this.updateViewModel;

        this.updateViewModel();
    }

    ngOnDestroy(): void {
        this.pod.updateHandler = noop;
    }

    private updateViewModel = (resetOptionsRef = false): void => {
        const data = this.getGridData(resetOptionsRef);
        this.vm$.next(data);
    };

    private getGridData(resetOptionsRef = false): TopEventsOutrightData | undefined {
        const event = this.pod.events[0];

        if (!event) {
            return undefined;
        }

        // event offer must be filtered before being assigned to the pod (done by topEventsWidgetComponent)
        // else not found optionGroup gets first place (index: -1)
        const optionGroup = sortBy(event.optionGroups, (og) =>
            indexOf([...this.pod.sourceFixtures[event.id].optionGroupIds], og.originalId ?? og.id),
        )[0];

        // online options first, ascending by odds
        optionGroup.options = sortBy(
            optionGroup.options,
            (o) => !o.online,
            (o) => toNumber(o.nativePrice.odds),
        ).slice(0, this.topEventsConfig.maxOptionsPerOutrightPod);

        optionGroup.options.forEach((data) => {
            if (event.offerSource === OfferSource.V2) {
                const playerId = data.playerId || data.optionPlayerId;
                const player = event.players?.find((p) => p.fixtureParticipantId === playerId);
                const participant = event.participants?.find((p) => p.fixtureParticipantId === playerId);
                if (player) {
                    data.playerId = playerId;
                    data.image = event.players.find((p) => p.id === player.teamId)?.image ?? undefined;
                    data.teamId = player.teamId;
                } else if (participant) {
                    data.playerId = participant.id ?? data.playerId;
                    data.image = participant.image ?? undefined;
                }
            } else {
                data.image = event.participants?.find((participant) => participant.id === data.playerId)?.image ?? undefined;
            }
        });

        const [enableOptionImages, optionImages] = this.participantImageService.showOrHideOptionImages(
            optionGroup.options,
            event.sport.id,
            event.offerSource,
        );

        const visibleOptionsLimit = this.optionsExpanded ? this.topEventsConfig.maxOptionsPerOutrightPod : this.getOptionsLimits(optionGroup);
        const showMoreLessButtonVisible = visibleOptionsLimit < optionGroup.options.length;

        const winMarketName = this.eachWayMarketHelper.getWinMarketName(optionGroup, event);

        return {
            event,
            // cloning triggers the rerendering of OptionGroupRenderer to calculate columns
            optionGroup: resetOptionsRef ? clone(optionGroup) : optionGroup,
            enableOptionImages,
            optionImages,
            trackingSource: `market/${optionGroup.name}|${PodType.Outright}`,

            title: winMarketName ? `${optionGroup.name} ${winMarketName}` : optionGroup.name,
            eventUrl: this.urlHelper.getEventUrl(event),
            visibleOptionsLimit,
            showMoreLessButtonVisible,
            expanded: !showMoreLessButtonVisible,
            hideMarketHeader: this.pod.hideMarketHeader,
            showAllWagersLink: this.pod.showAllWagersLink,
        };
    }

    private getOptionsLimits(optionGroup: EventOptionGroup): number {
        return isDefined(this.pod.marketsBeforeShowMore) &&
            this.pod.marketsBeforeShowMore > 0 &&
            this.pod.marketsBeforeShowMore < this.topEventsConfig.maxOptionsPerOutrightPod &&
            this.pod.marketsBeforeShowMore < optionGroup?.options.length
            ? this.pod.marketsBeforeShowMore
            : this.topEventsConfig.maxOptionsPerOutrightPod;
    }

    showMore(): void {
        this.topEventsTrackingService.trackShowMoreLess(this.pod, this.index, false);
        this.optionsExpanded = true;
        this.updateViewModel(true);
    }

    trackBottomLink(): void {
        this.topEventsTrackingService.trackBottomLink(this.pod, this.index);
    }
}
